import { collection, doc, setDoc, deleteDoc, getDoc, getDocs, query, where } from 'firebase/firestore';
import logger from './logger';

/**
 * Generate a unique fingerprint for duplicate detection
 * Encode to make it safe for Firestore document IDs (no /)
 */
export const generateFingerprint = (bill) => {
  const facility = (bill.site_name || '').toLowerCase().trim();
  const date = bill.date || '';
  const type = (bill.type || '').toLowerCase().trim();
  const provider = (bill.provider || '').toLowerCase().trim();
  const amount = parseFloat(bill.amount || 0).toFixed(2);
  
  const fingerprint = `${facility}|${date}|${type}|${provider}|${amount}`;
  
  // Base64 encode to avoid special characters in Firestore doc ID
  // Use btoa (browser) - remove padding characters as they're not needed for unique IDs
  const encoded = btoa(fingerprint);
  return encoded.replace(/\//g, '_').replace(/\+/g, '-').replace(/=/g, '');
};

/**
 * Update duplicate_fingerprints when a bill is added or updated
 */
export const updateDuplicateFingerprint = async (db, bill) => {
  const fingerprint = generateFingerprint(bill);
  const fingerprintRef = doc(db, 'duplicate_fingerprints', fingerprint);
  
  try {
    const fingerprintDoc = await getDoc(fingerprintRef);
    
    if (fingerprintDoc.exists()) {
      // Add this bill ID if not already in the array
      const data = fingerprintDoc.data();
      const billIds = data.billIds || [];
      
      if (!billIds.includes(bill.id)) {
        billIds.push(bill.id);
        await setDoc(fingerprintRef, {
          fingerprint,
          facility: bill.site_name || '',
          date: bill.date || '',
          type: bill.type || '',
          provider: bill.provider || '',
          amount: parseFloat(bill.amount || 0).toFixed(2),
          billIds,
          count: billIds.length,
          lastUpdated: new Date()
        });
      }
    } else {
      // Create new fingerprint document
      await setDoc(fingerprintRef, {
        fingerprint,
        facility: bill.site_name || '',
        date: bill.date || '',
        type: bill.type || '',
        provider: bill.provider || '',
        amount: parseFloat(bill.amount || 0).toFixed(2),
        billIds: [bill.id],
        count: 1,
        lastUpdated: new Date()
      });
    }
  } catch (error) {
    logger.error('Error updating duplicate fingerprint:', error);
    // Don't fail the bill save if fingerprint update fails
  }
};

/**
 * Remove a bill from duplicate_fingerprints when deleted or updated
 */
export const removeDuplicateFingerprint = async (db, bill) => {
  const fingerprint = generateFingerprint(bill);
  const fingerprintRef = doc(db, 'duplicate_fingerprints', fingerprint);
  
  try {
    const fingerprintDoc = await getDoc(fingerprintRef);
    
    if (fingerprintDoc.exists()) {
      const data = fingerprintDoc.data();
      const billIds = (data.billIds || []).filter(id => id !== bill.id);
      
      if (billIds.length === 0) {
        // No more bills with this fingerprint - delete the document
        await deleteDoc(fingerprintRef);
      } else {
        // Update with remaining bill IDs
        await setDoc(fingerprintRef, {
          ...data,
          billIds,
          count: billIds.length,
          lastUpdated: new Date()
        });
      }
    }
  } catch (error) {
    logger.error('Error removing duplicate fingerprint:', error);
    // Don't fail the bill delete if fingerprint removal fails
  }
};

/**
 * Get all duplicate groups from the fingerprints collection
 * OPTIMIZED: Only fetches documents where count > 1 (actual duplicates)
 */
export const getDuplicateGroups = async (db, ignoredDuplicates = []) => {
  try {
    // Only fetch fingerprints that have duplicates (count > 1)
    const duplicatesQuery = query(
      collection(db, 'duplicate_fingerprints'),
      where('count', '>', 1)
    );
    const fingerprintsSnapshot = await getDocs(duplicatesQuery);
    const duplicates = [];
    
    fingerprintsSnapshot.docs.forEach(doc => {
      const data = doc.data();
      
      // Skip if ignored
      if (!ignoredDuplicates.includes(data.fingerprint)) {
        duplicates.push({
          fingerprint: data.fingerprint,
          billIds: data.billIds || [],
          facility: data.facility,
          date: data.date,
          type: data.type,
          provider: data.provider,
          amount: data.amount
        });
      }
    });
    
    return duplicates;
  } catch (error) {
    logger.error('Error fetching duplicate groups:', error);
    return [];
  }
};
